import { NegativeColors, TextColors } from '@picsart/design-system/foundation/colors';
import {
  TypographyT4,
  TypographyT6,
  TypographyT7,
  TypographyT8,
} from '@picsart/design-system/foundation/typography';

import { useMemo } from 'react';

import classnames from 'classnames';

import { IconAIGen } from '@picsart/design-system/foundation/icons/IconAIGen';
import { IconAnimation } from '@picsart/design-system/foundation/icons/IconAnimation';
import { IconWorkspace } from '@picsart/design-system/foundation/icons/IconWorkspace';
import { IconInfoCircleOutline } from '@picsart/design-system/foundation/icons/IconInfoCircleOutline';

import { Badge, BadgeTypes } from '@picsart/design-system/components/Badge';
import { Text, TextElementTypes } from '@picsart/design-system/components/Text';
import { Placements, Tooltip } from '@picsart/design-system/components/Tooltip';
import { FontWeights } from '@picsart/design-system/foundation/fonts';

import Description from 'templates/components/SingleTemplate/components/Description';
import {
  ANALYTICS_NAMES,
  animateStaticTemplates,
  commercialTitle,
  designDetailsText,
  generateAiTemplates,
  generateAiTemplatesUrl,
  generateAnimateStaticTemplatesUrl,
  generateLocalizeTemplatesUrl,
  licenseCommercialText,
  licenseNotCommercialText,
  licenseTitle,
  localizeTemplates,
  sizeText,
} from 'templates/components/SingleTemplate/constants';

import { useTranslator } from '@picsart/rc/services/localization';

import useDevice from 'hooks/useDeviceType';
import useSingleTemplatesDataStore from 'templates/stores/singleTemplateDataStore';
import EditAndShare from '../EditAndShare';
import RelevantCategories from '../RelevantCategories';
import useStyles from './style';
import testIds from './testIds';

const Aside = () => {
  const styles = useStyles();

  const { templateData } = useSingleTemplatesDataStore(state => ({
    templateData: state.singleTemplateData,
  }));

  const {
    preview_info: { width, height },
    id,
    paid,
    display_name,
    license: { type },
    comment,
    subtypes = [],
    classes = [],
  } = templateData;

  const categories = useMemo(() => ({ subtypes, classes }), [subtypes, classes]);

  const translate = useTranslator();
  const { isMobile } = useDevice();

  return (
    <section className={styles.section}>
      <div
        className={styles.sidebar}
        data-testid={testIds.root}
        data-pulse-section={ANALYTICS_NAMES.ASIDE}
      >
        <div className={styles.topPart}>
          {paid && <Badge variant={BadgeTypes.Plus} title="Plus" />}
          <div className={styles.t2}>
            <Text
              data-testid={testIds.templateDisplayName}
              elementType={TextElementTypes.H1}
              size={isMobile ? TypographyT7 : TypographyT8}
              weight={FontWeights.bold}
              color={TextColors.base}
            >
              {translate(display_name) || ''}
            </Text>
          </div>
          {!isMobile && (
            <div className={styles.topPart}>
              <EditAndShare templateId={id} object_id={id} />
            </div>
          )}
          <div className={classnames(styles.tCommon, styles.t6)}>
            <IconAIGen className={styles.tIcon} />
            <a
              href={generateAiTemplatesUrl(id)}
              className={styles.tLink}
              target="_blank"
              rel="noreferrer"
            >
              {translate(generateAiTemplates)}
            </a>
          </div>
          <div className={classnames(styles.tCommon)}>
            <IconAnimation className={styles.tIcon} />
            <a
              href={generateAnimateStaticTemplatesUrl}
              className={styles.tLink}
              target="_blank"
              rel="noreferrer"
            >
              {translate(animateStaticTemplates)}
            </a>
          </div>
          <div className={classnames(styles.tCommon)}>
            <IconWorkspace className={styles.tIcon} />
            <a
              href={generateLocalizeTemplatesUrl(id)}
              className={styles.tLink}
              target="_blank"
              rel="noreferrer"
            >
              {translate(localizeTemplates)}
            </a>
          </div>
        </div>
        <div className={styles.bottomPart} data-pulse-section={ANALYTICS_NAMES.RELEVANT_CATEGORIES}>
          <div className={styles.designDetails}>
            <Text size={TypographyT6} weight={FontWeights.semibold} color={TextColors.base}>
              {translate(designDetailsText)}
            </Text>
            <div className={styles.detailsBlockSize}>
              <Text size={TypographyT4} weight={FontWeights.semibold} color={TextColors.base}>
                {translate(sizeText)}
              </Text>
              <Text size={TypographyT4} weight={FontWeights.medium} color={TextColors.tint1}>
                {width} x {height} px
              </Text>
            </div>
            <div className={styles.detailsBlockLicense}>
              <Text size={TypographyT4} weight={FontWeights.semibold} color={TextColors.base}>
                {translate(licenseTitle)}
              </Text>
              <div className={styles.license}>
                <Text
                  size={TypographyT4}
                  weight={FontWeights.medium}
                  color={TextColors.tint1}
                  className={styles.licenseText}
                >
                  {translate(type) || ''}
                </Text>
                {type && (
                  <Tooltip
                    zIndex={10}
                    placement={Placements.Top}
                    target={
                      <IconInfoCircleOutline className={styles.infoIcon} fill={TextColors.tint2} />
                    }
                  >
                    <Text
                      className={styles.tooltipText}
                      elementType={TextElementTypes.Span}
                      color={NegativeColors.text.default}
                    >
                      {type.toLocaleLowerCase() === commercialTitle
                        ? translate(licenseCommercialText)
                        : translate(licenseNotCommercialText)}
                    </Text>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
          <RelevantCategories categories={categories} />
          {isMobile && comment && <Description content={comment} />}
        </div>
        {isMobile && (
          <div
            className={styles.mobileEditAndShare}
            data-testid={testIds.mobileEditAndShareButtons}
            id="sticky-button"
          >
            <EditAndShare templateId={id} object_id={id} />
          </div>
        )}
      </div>
    </section>
  );
};
export default Aside;
